/* ──────────────────────────────────────────────────────────────
 *  Details-field mapping per bushing type
 *
 *  Maps internal column names → human-readable labels shown in
 *  the "More Details" dialog. Extracted from the Python
 *  DETAILS_FIELD_MAPPING_* class attributes.
 * ────────────────────────────────────────────────────────────── */

import type { BushingType } from '@/types/bushing';
import type { FieldMapping } from '@/types/bushing';

const DETAILS_FIELD_MAPPING_A1: FieldMapping = {
  SHA: 'Shore',
  SWG_PCT: 'Swaging [%]',
  RADIAL_SWAGING_MM: 'Diameter Swaging [mm]',
  RUBBER_HEIGHT_HOT: 'Rubber height hot [mm]',
  CORE_LENGTH: 'Inner core length [mm]',
  CONTROL_ARM_ID: 'Control arm ID [mm]',
  BUSH_OD_HOT: 'Bush OD hot [mm]',
  SLEEVE_THK: 'Outer sleeve thickness [mm]',
  _core_id_input: 'Inner core ID [mm]',
  CORE_OD: 'Inner core OD [mm]',
  _clamp_load_input: 'Clamp load [kN]',
  SLEEVE_LENGTH: 'Outer sleeve length [mm]',
};

const DETAILS_FIELD_MAPPING_B1: FieldMapping = {
  SHA: 'Shore',
  SWG_PCT: 'Swaging [%]',
  DIAMETER_SWAGING: 'Diameter Swaging [mm]',
  RUBBER_HEIGHT_HOT: 'Rubber height hot [mm]',
  CORE_LENGTH: 'Inner core length [mm]',
  OUTER_SLEEVE_DIAMETER_MOUNTED: 'Control arm ID [mm]',
  BUSH_OD_HOT: 'Bush OD hot [mm]',
  SLEEVE_THK: 'Outer sleeve thickness [mm]',
  _core_id_input: 'Inner core ID [mm]',
  CORE_OUTER_DIAMETER: 'Inner core OD [mm]',
  _clamp_load_input: 'Clamp load [kN]',
  OUTER_SLEEVE_LENGTH: 'Outer sleeve length [mm]',
};

const DETAILS_FIELD_MAPPING_C1: FieldMapping = {
  SHA: 'Shore',
  SWG_PCT: 'Swaging [%]',
  RADIAL_SWAGING_MM: 'Diameter Swaging [mm]',
  RUBBER_HEIGHT_HOT: 'Rubber height hot [mm]',
  CORE_LENGTH: 'Inner core length [mm]',
  CONTROL_ARM_ID: 'Control arm ID [mm]',
  BUSH_OD_HOT: 'Bush OD hot [mm]',
  SLEEVE_THK: 'Outer sleeve thickness [mm]',
  _core_id_input: 'Inner core ID [mm]',
  CORE_OUTER_DIAMETER: 'Inner core OD [mm]',
  _clamp_load_input: 'Clamp load [kN]',
  OUTER_SLEEVE_LENGTH: 'Outer sleeve length [mm]',
};

const DETAILS_FIELD_MAPPING_D1: FieldMapping = {
  SHA: 'Shore',
  SWG_PCT: 'Swaging [%]',
  DIAMETER_SWAGING: 'Diameter Swaging [mm]',
  RUBBER_HEIGHT_HOT: 'Rubber height hot [mm]',
  LENGTH_ARM_INNER_CORE: 'Inner core length [mm]',
  CONTROL_ARM_ID: 'Control arm ID [mm]',
  BUSH_OD_HOT: 'Bush OD hot [mm]',
  SLEEVE_THK: 'Outer sleeve thickness [mm]',
  _core_id_input: 'Inner core ID [mm]',
  DIAM_CENTER_INNER_CORE: 'Inner core OD [mm]',
  _clamp_load_input: 'Clamp load [kN]',
  LENGTH_ARM_OUTER_CORE: 'Outer sleeve length [mm]',
};

const DETAILS_FIELD_MAPPING_BYD_VOIDED: FieldMapping = {
  SHA: 'Shore',
  SWG_PCT: 'Swaging [%]',
  DIAMETER_SWAGING: 'Diameter Swaging [mm]',
  RUBBER_HEIGHT_HOT: 'Rubber height hot [mm]',
  LENGTH_ARM_INNER_CORE: 'Inner core length [mm]',
  CONTROL_ARM_ID: 'Control arm ID [mm]',
  BUSH_OD_HOT: 'Bush OD hot [mm]',
  SLEEVE_THK: 'Outer sleeve thickness [mm]',
  _core_id_input: 'Inner core ID [mm]',
  DIAM_CENTER_INNER_CORE: 'Inner core OD [mm]',
  _clamp_load_input: 'Clamp load [kN]',
  LENGTH_ARM_OUTER_CORE: 'Outer sleeve length [mm]',
};

const DETAILS_FIELD_MAPPING_BYD_SOLID: FieldMapping = {
  SHA: 'Shore',
  SWG_PCT: 'Swaging [%]',
  DIAMETER_SWAGING: 'Diameter Swaging [mm]',
  RUBBER_HEIGHT_HOT: 'Rubber height hot [mm]',
  LENGTH_INNER_CORE: 'Inner core length [mm]',
  CONTROL_ARM_ID: 'Control arm ID [mm]',
  BUSH_OD_HOT: 'Bush OD hot [mm]',
  SLEEVE_THK: 'Outer sleeve thickness [mm]',
  _core_id_input: 'Inner core ID [mm]',
  DIAM_CENTER_INNER_CORE: 'Inner core OD [mm]',
  _clamp_load_input: 'Clamp load [kN]',
  LENGTH_OUTER_CORE: 'Outer sleeve length [mm]',
};

/** Lookup details-field mapping by bushing type. */
export const DETAILS_FIELD_MAPPINGS: Record<BushingType, FieldMapping> = {
  A1: DETAILS_FIELD_MAPPING_A1,
  B1: DETAILS_FIELD_MAPPING_B1,
  C1: DETAILS_FIELD_MAPPING_C1,
  D1: DETAILS_FIELD_MAPPING_D1,
  'BYD EV Voided': DETAILS_FIELD_MAPPING_BYD_VOIDED,
  'BYD EV Solid': DETAILS_FIELD_MAPPING_BYD_SOLID,
};

/** SOR metadata display labels (for SOR upload result panel). */
export const DISPLAY_LABELS_META: Record<string, string> = {
  part_name: 'Part Name',
  outer_sleeve_length: 'Outer sleeve length',
  core_length: 'Core length',
  control_arm_id: 'Control arm / Test fixture ID',
  core_id: 'Core ID',
  core_od_min: 'Core OD Min',
  core_od_max: 'Core OD Max',
  min_area_explicit: 'Min Area (Explicit)',
  min_area_calculated: 'Min Area (Calculated)',
  flange_od: 'Flange OD',
  radial_i: 'Radial - I',
  radial_ii: 'Radial - II',
  axial: 'Axial Stiffness',
  torsion: 'Torsional Stiffness',
  conical_i: 'Conical Stiffness - I',
  conical_ii: 'Conical Stiffness - II',
  radial_i_amp: 'Test Amplitude (Radial - I)',
  radial_ii_amp: 'Test Amplitude (Radial - II)',
  axial_amp: 'Test Amplitude (Axial Stiffness)',
  torsion_amp: 'Test Amplitude (Torsional Stiffness)',
  clamp_load: 'Clamping force',
  core_material: 'Core - material',
  outer_material: 'Outer sleeve - material',
};

export const META_DISPLAY: Record<string, string> = {
  customer: 'Customer',
  vehicle_platform: 'Vehicle/platform',
  product_group: 'Product Group',
  sop: 'SOP',
};
